import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const BankReconciliation = () => {
  const [bank, setBank] = useState('DEFAULT BANK');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('bank', bank);
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      const res = await api.get(`/accounting-reports/bank-reconciliation?${params}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '-';

  const sectionHeader = { background: '#5f9ea0', color: '#fff', padding: '8px 16px', fontSize: '14px', fontWeight: 700, borderRadius: '4px 4px 0 0', marginTop: '20px' };
  const summaryBox = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0 0 4px 4px', padding: '20px' };
  const labelStyle = { fontSize: '13px', fontWeight: 500, textAlign: 'right', paddingRight: '12px', color: '#374151' };
  const valueStyle = { fontSize: '13px', padding: '4px 8px', background: '#e5e7eb', borderRadius: '4px', minWidth: '120px', textAlign: 'right' };

  return (
    <div className="card" style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '20px' }}>Bank Reconciliation</h2>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600 }}>Bank</label>
          <select value={bank} onChange={(e) => setBank(e.target.value)}
            style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}>
            <option value="DEFAULT BANK">DEFAULT BANK</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600 }}>Period</label>
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
          <span>~</span>
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }} />
        </div>
        <button onClick={handleCalculate} disabled={loading}
          style={{ padding: '8px 24px', backgroundColor: '#5f9ea0', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
          {loading ? 'Calculating...' : 'Calculate'}
        </button>
      </div>

      {data && (
        <>
          {/* Balance Summary */}
          <div style={sectionHeader}>Balance Summary</div>
          <div style={summaryBox}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '32px' }}>
              {/* Statement Balance */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, marginBottom: '12px' }}>STATEMENT BALANCE</h4>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <tbody>
                    <tr><td style={labelStyle}>Beginning Balance</td><td style={valueStyle}>{fmt(data.statement_balance.beginning_balance)}</td></tr>
                    <tr><td style={labelStyle}>Deposit and Credit</td><td style={valueStyle}>{fmt(data.statement_balance.deposits_credits)}</td></tr>
                    <tr><td style={labelStyle}>Checks and Debit</td><td style={valueStyle}>{fmt(data.statement_balance.checks_debits)}</td></tr>
                    <tr><td style={labelStyle}>Ending Balance</td><td style={valueStyle}>{fmt(data.statement_balance.ending_balance)}</td></tr>
                  </tbody>
                </table>
              </div>
              {/* Outstanding */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, marginBottom: '12px' }}>OUTSTANDING</h4>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <tbody>
                    <tr><td style={labelStyle}>Deposit and Credit</td><td style={valueStyle}>{fmt(data.outstanding.deposits_credits)}</td></tr>
                    <tr><td style={labelStyle}>Checks and Debit</td><td style={valueStyle}>{fmt(data.outstanding.checks_debits)}</td></tr>
                    <tr><td style={labelStyle}><span style={{ color: '#2563eb' }}>Actual Ending Balance</span></td><td style={valueStyle}>{fmt(data.outstanding.actual_ending_balance)}</td></tr>
                  </tbody>
                </table>
              </div>
              {/* Book Balance */}
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 700, marginBottom: '12px' }}>BOOK BALANCE</h4>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <tbody>
                    <tr><td style={labelStyle}>Beginning Balance</td><td style={valueStyle}>{fmt(data.book_balance.beginning_balance)}</td></tr>
                    <tr><td style={labelStyle}>Deposit and Credit</td><td style={valueStyle}>{fmt(data.book_balance.deposits_credits)}</td></tr>
                    <tr><td style={labelStyle}>Checks and Debit</td><td style={valueStyle}>{fmt(data.book_balance.checks_debits)}</td></tr>
                    <tr><td style={labelStyle}><span style={{ color: '#2563eb' }}>Ending Balance</span></td><td style={valueStyle}>{fmt(data.book_balance.ending_balance)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ marginTop: '16px', fontSize: '14px', fontWeight: 700 }}>
              BANK &amp; BOOK DIFFERENCE: <span style={{ color: '#2563eb' }}>{fmt(data.difference)}</span>
            </div>
          </div>

          {/* Outstanding Detail */}
          <div style={sectionHeader}>Outstanding Detail</div>
          <div style={summaryBox}>
            {/* Deposit & Credit */}
            <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Deposit &amp; Credit</h4>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <thead>
                <tr style={{ background: '#5f9ea0', color: '#fff' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Post Date</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Check No.</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Received From</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Currency</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Deposit</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Deposit Date</th>
                </tr>
              </thead>
              <tbody>
                {data.outstanding_deposits.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>No Data Available</td></tr>
                ) : data.outstanding_deposits.map(d => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}>{fmtDate(d.post_date)}</td>
                    <td style={{ padding: '6px 8px' }}>{d.check_no || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{d.party || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{d.bank_currency || 'USD'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(d.total_amount)}</td>
                    <td style={{ padding: '6px 8px' }}>{d.deposit_date ? 'Yes' : ''}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtDate(d.deposit_date)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '8px', textAlign: 'right' }}>Total</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#059669' }}>
                    {fmt(data.outstanding_deposits.reduce((s, d) => s + (Number(d.total_amount) || 0), 0))}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>

            {/* Checks & Debit */}
            <h4 style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>Checks &amp; Debit</h4>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#5f9ea0', color: '#fff' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Post Date</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Check No.</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Pay To</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Currency</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Clear</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Clear Date</th>
                </tr>
              </thead>
              <tbody>
                {data.outstanding_checks.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '12px', textAlign: 'center', color: '#94a3b8' }}>No Data Available</td></tr>
                ) : data.outstanding_checks.map(c => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 8px' }}>{fmtDate(c.post_date)}</td>
                    <td style={{ padding: '6px 8px' }}>{c.check_no || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{c.party || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{c.bank_currency || 'USD'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmt(c.total_amount)}</td>
                    <td style={{ padding: '6px 8px' }}>{c.deposit_date ? 'Yes' : ''}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtDate(c.deposit_date)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '8px', textAlign: 'right' }}>Total</td>
                  <td style={{ padding: '8px', textAlign: 'right', color: '#059669' }}>
                    {fmt(data.outstanding_checks.reduce((s, c) => s + (Number(c.total_amount) || 0), 0))}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default BankReconciliation;
